import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Project detail view: the roadmap + task list for a single project, plus its
// related-resources panel, inline task creation, the add-task modal, and the
// project-header editors (BU/activity, name, due date, priority, assignees).
//
// Extracted from the oversized pages/Projects.tsx (P2 compartmentalization).
// Self-contained: pulls TaskRow + DescriptionEditor from ./TaskRow and shared
// controls/types from the usual places — no back-references into Projects.tsx.
// Only ProjectDetail is exported (ProjectsPage mounts it); the rest are
// module-internal. Pure move — behavior unchanged.
import { notify } from "../../components/Toast";
import { Fragment, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import MultiSelect from "../../components/MultiSelect";
import ProgressBar from "../../components/ProgressBar";
import RoadmapView from "../../components/RoadmapView";
import { safeHref } from "../../lib/safeUrl";
import { OneTimeMeetingPanel } from "./Sidebar";
import { DescriptionEditor, TaskRow } from "./TaskRow";
// Compact "Xh Ym" formatter for the project header time card + per-task
// time pills. Zero minutes shows "0m" (not "0h 0m") and exact-hour values
// drop the minutes ("2h", not "2h 0m"). 15-min minimum rounding lives at
// the API edge — we just render what we're given.
export function formatMinutes(total) {
    const m = Math.max(0, Math.round(total));
    if (m === 0)
        return "0m";
    const h = Math.floor(m / 60);
    const mm = m % 60;
    if (h === 0)
        return `${mm}m`;
    if (mm === 0)
        return `${h}h`;
    return `${h}h ${mm}m`;
}
// Order a standard project's top-level tasks for the detail list (Concept A):
// independent tasks first in creation order (newest last), then dependent
// tasks in topological order — each appears after the prerequisites it waits
// on. Returns the flat ordered list plus how many lead the "Ready" group.
function orderProjectTasks(tasks) {
    const idSet = new Set(tasks.map((t) => t.id));
    // Only count predecessors that are themselves in this list.
    const predsOf = (t) => (t.predecessor_task_ids ?? []).filter((p) => idSet.has(p));
    const byCreated = (a, b) => {
        const ca = a.created_at ?? "";
        const cb = b.created_at ?? "";
        if (ca !== cb)
            return ca < cb ? -1 : 1;
        return a.order_index - b.order_index;
    };
    const ready = tasks.filter((t) => predsOf(t).length === 0).sort(byCreated);
    const waitingPool = tasks
        .filter((t) => predsOf(t).length > 0)
        .sort(byCreated);
    // Topological emit: take the earliest-created waiting task whose present
    // prerequisites are all already emitted. If none qualify (a dependency
    // cycle), take the earliest remaining so we never loop forever.
    const emitted = new Set(ready.map((t) => t.id));
    const waiting = [];
    const remaining = [...waitingPool];
    let guard = 0;
    while (remaining.length > 0 && guard++ < 2000) {
        let idx = remaining.findIndex((t) => predsOf(t).every((p) => emitted.has(p)));
        if (idx < 0)
            idx = 0;
        const [picked] = remaining.splice(idx, 1);
        waiting.push(picked);
        emitted.add(picked.id);
    }
    return { ordered: [...ready, ...waiting], readyCount: ready.length };
}
function ProjectResourcesPanel({ projectId, canManage, }) {
    const [linked, setLinked] = useState([]);
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [picking, setPicking] = useState(false);
    const [allResources, setAllResources] = useState(null);
    // Ad-hoc "project-only link" add form state.
    const [addingLink, setAddingLink] = useState(false);
    const [linkTitle, setLinkTitle] = useState("");
    const [linkUrl, setLinkUrl] = useState("");
    const [savingLink, setSavingLink] = useState(false);
    async function load() {
        try {
            const [rows, linkRows] = await Promise.all([
                api.get(`/projects/${projectId}/resources`),
                api.get(`/projects/${projectId}/links`),
            ]);
            setLinked(rows);
            setLinks(linkRows);
        }
        catch {
            /* leave lists empty on failure — panel just shows the empty state */
        }
        finally {
            setLoading(false);
        }
    }
    async function addLink() {
        if (!linkTitle.trim() || !linkUrl.trim())
            return;
        setSavingLink(true);
        try {
            await api.post(`/projects/${projectId}/links`, {
                title: linkTitle.trim(),
                url: linkUrl.trim(),
            });
            setLinkTitle("");
            setLinkUrl("");
            setAddingLink(false);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't add link");
        }
        finally {
            setSavingLink(false);
        }
    }
    async function removeLink(id) {
        try {
            await api.del(`/projects/${projectId}/links/${id}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't remove link");
        }
    }
    useEffect(() => {
        setLoading(true);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);
    async function openPicker() {
        setPicking(true);
        if (allResources === null) {
            try {
                setAllResources(await api.get("/resources"));
            }
            catch {
                setAllResources([]);
            }
        }
    }
    async function link(resourceId) {
        try {
            await api.post(`/projects/${projectId}/resources`, {
                resource_id: resourceId,
            });
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't link resource");
        }
    }
    async function unlink(resourceId) {
        try {
            await api.del(`/projects/${projectId}/resources/${resourceId}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't remove resource");
        }
    }
    if (loading)
        return null;
    // Nothing to show and the viewer can't curate — don't clutter the page.
    if (linked.length === 0 && links.length === 0 && !canManage)
        return null;
    const linkedIds = new Set(linked.map((r) => r.id));
    const available = (allResources ?? []).filter((r) => !linkedIds.has(r.id));
    return (_jsxs("section", { className: "project-resources", children: [_jsxs("div", { className: "project-resources-head", children: [_jsx("h3", { children: "Related resources" }), canManage && !picking && (_jsx("button", { type: "button", className: "btn-link", onClick: openPicker, children: "+ Link a resource" }))] }), linked.length === 0 && !picking && (_jsx("p", { className: "muted small", children: "No resources linked yet \u2014 pull in the docs and tools this project needs." })), linked.length > 0 && (_jsx("ul", { className: "project-resources-list", children: linked.map((r) => (_jsxs("li", { className: "project-resources-row", children: [_jsx("span", { className: "project-resources-icon", "aria-hidden": "true", children: r.icon || "🔗" }), _jsx("a", { href: safeHref(r.url), target: "_blank", rel: "noopener noreferrer", className: "project-resources-title", children: r.title }), _jsx("a", { href: safeHref(r.url), target: "_blank", rel: "noopener noreferrer", className: "project-resources-go", "aria-label": `Open ${r.title}`, children: "\u2197" }), canManage && (_jsx("button", { type: "button", className: "btn-link project-resources-unlink", onClick: () => unlink(r.id), children: "Remove" }))] }, r.id))) })), picking && (_jsxs("div", { className: "project-resources-picker", children: [_jsxs("div", { className: "project-resources-picker-head", children: [_jsx("span", { className: "muted small", children: "Pick a resource to link" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => setPicking(false), children: "Done" })] }), allResources === null ? (_jsx("p", { className: "muted small", children: "Loading\u2026" })) : available.length === 0 ? (_jsx("p", { className: "muted small", children: "Nothing left to link \u2014 every resource is already on this project, or none exist yet." })) : (_jsx("ul", { className: "project-resources-pick-list", children: available.map((r) => (_jsxs("li", { className: "project-resources-pick-row", children: [_jsx("span", { "aria-hidden": "true", children: r.icon || "🔗" }), _jsx("span", { className: "project-resources-pick-title", children: r.title }), _jsx("button", { type: "button", className: "today-schedule-btn", onClick: () => link(r.id), children: "Link" })] }, r.id))) }))] })), _jsxs("div", { className: "project-links", children: [_jsxs("div", { className: "project-resources-head", children: [_jsx("h4", { className: "project-links-head", children: "Project-only links" }), canManage && !addingLink && (_jsx("button", { type: "button", className: "btn-link", onClick: () => setAddingLink(true), children: "+ Add link" }))] }), links.length === 0 && !addingLink && (_jsx("p", { className: "muted small", children: "One-off links for just this project \u2014 not added to the Resources library." })), links.length > 0 && (_jsx("ul", { className: "project-resources-list", children: links.map((l) => (_jsxs("li", { className: "project-resources-row", children: [_jsx("span", { className: "project-resources-icon", "aria-hidden": "true", children: "\uD83D\uDD17" }), _jsx("a", { href: safeHref(l.url), target: "_blank", rel: "noopener noreferrer", className: "project-resources-title", children: l.title }), _jsx("a", { href: safeHref(l.url), target: "_blank", rel: "noopener noreferrer", className: "project-resources-go", "aria-label": `Open ${l.title}`, children: "\u2197" }), canManage && (_jsx("button", { type: "button", className: "btn-link project-resources-unlink", onClick: () => removeLink(l.id), children: "Remove" }))] }, l.id))) })), addingLink && (_jsxs("form", { className: "project-links-add", onSubmit: (e) => {
                            e.preventDefault();
                            void addLink();
                        }, children: [_jsx("input", { type: "text", placeholder: "Label (e.g. Client Figma file)", value: linkTitle, onChange: (e) => setLinkTitle(e.target.value), maxLength: 200, autoFocus: true }), _jsx("input", { type: "url", placeholder: "https://\u2026", value: linkUrl, onChange: (e) => setLinkUrl(e.target.value), maxLength: 2000 }), _jsxs("div", { className: "project-links-add-row", children: [_jsx("button", { type: "submit", className: "btn-primary btn-small", disabled: savingLink || !linkTitle.trim() || !linkUrl.trim(), children: savingLink ? "Adding…" : "Add link" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => {
                                            setAddingLink(false);
                                            setLinkTitle("");
                                            setLinkUrl("");
                                        }, children: "Cancel" })] })] }))] })] }));
}
export function ProjectDetail({ tree, members, units, activities, canManage, currentUserId, onChanged, onDelete, }) {
    const unitName = units.find((u) => u.id === tree.business_unit_id)?.name;
    const activityName = activities.find((a) => a.id === tree.activity_id)?.name;
    // Recurring events don't have meaningful "progress" or "priority" semantics —
    // they happen forever on a cadence, not as a deliverable that completes.
    // Hide both the progress card and the priority card for that type.
    const isRecurring = tree.type === "recurring_event";
    const isOneTime = tree.type === "one_time_event";
    const isOneOffTask = tree.type === "one_off_task";
    // Roadmap renders inline at the top of every standard project — no
    // toggle; the dependency graph + bottleneck radar is the front view
    // and tasks live below it. Recurring meetings skip the roadmap entirely.
    // Header overflow menu (kebab) — closes on outside click. Used for the
    // less-frequent destructive action so the hero stays clean for the 95 %
    // of visits that aren't trying to delete the project.
    const [menuOpen, setMenuOpen] = useState(false);
    useEffect(() => {
        if (!menuOpen)
            return;
        function onDoc() {
            setMenuOpen(false);
        }
        document.addEventListener("click", onDoc);
        return () => document.removeEventListener("click", onDoc);
    }, [menuOpen]);
    // "Ready to confirm?" banner — fires whenever the project is still
    // active but every task on it is closed. Mirrors the server-side
    // detector that drops the project_completion_ready notification on
    // admin + BU managers. We compute the condition locally rather than
    // relying on the notification existing so the banner appears even on
    // a fresh page load where the notification was already dismissed —
    // the work is still done, the confirm is still needed.
    const tasksOnProject = tree.tasks ?? [];
    // One-off tasks are ops/admin/marketing work — not a deliverable —
    // so we don't surface the "Ready to confirm completion?" banner for
    // them either. Mirrors the server-side gate on Momentum emit + the
    // completion-ready notification (notify.py).
    const allTasksDone = tree.status === "active" &&
        !isRecurring &&
        !isOneTime &&
        !isOneOffTask &&
        tasksOnProject.length > 0 &&
        tasksOnProject.every((t) => t.status === "completed");
    async function confirmComplete() {
        if (!confirm(`Confirm "${tree.name}" is complete? It'll be celebrated on Momentum and move out of the active list.`)) {
            return;
        }
        try {
            await api.patch(`/projects/${tree.id}`, { status: "completed" });
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't complete the project.");
        }
    }
    return (_jsxs("div", { className: "project-detail-root", children: [allTasksDone && (_jsxs("div", { className: "project-confirm-banner", role: "status", children: [_jsxs("div", { className: "project-confirm-banner-text", children: [_jsx("span", { className: "project-confirm-banner-title", children: "\uD83C\uDF89 All tasks complete" }), _jsx("span", { className: "project-confirm-banner-sub", children: canManage
                                    ? "Confirm to wrap this project and celebrate it on Momentum."
                                    : "Waiting on an admin or your business unit manager to confirm." })] }), canManage && (_jsx("button", { type: "button", className: "btn-primary project-confirm-banner-btn", onClick: confirmComplete, children: "Confirm project complete" }))] })), _jsxs("header", { className: "project-hero v2", children: [_jsxs("div", { className: "project-hero-toprow", children: [_jsxs("div", { className: "project-hero-titleblock", children: [_jsxs("div", { className: "project-hero-eyebrow", children: [isRecurring
                                                ? "Recurring meeting"
                                                : isOneTime
                                                    ? "One-time event"
                                                    : isOneOffTask
                                                        ? "One-off task"
                                                        : "Project", _jsx("span", { children: " \u00B7 " }), _jsx(ProjectBuActivityEditor, { tree: tree, units: units, activities: activities, canEdit: canManage, onChanged: onChanged, unitName: unitName, activityName: activityName })] }), _jsx(ProjectNameEditor, { tree: tree, canEdit: canManage, onChanged: onChanged }), !isRecurring && !isOneTime && (_jsxs("div", { className: "project-hero-metaline", children: [_jsxs("span", { className: "project-hero-meta-progress", children: [_jsx(ProgressBar, { value: tree.progress_percent }), _jsxs("span", { className: "muted small", children: [tree.progress_percent, "% complete"] })] }), _jsxs("span", { className: "project-hero-meta-due", children: [_jsx("span", { className: "meta-label", children: "Due:" }), _jsx(ProjectDueDateRow, { tree: tree, isAdmin: canManage, onChanged: onChanged })] }), _jsxs("span", { className: "project-hero-meta-priority", children: [_jsx("span", { className: "meta-label", title: "1 = most urgent, 10 = least urgent", children: "Urgency Rating:" }), _jsx(ProjectPriorityRow, { tree: tree, isAdmin: canManage, onChanged: onChanged })] }), _jsxs("span", { className: "project-hero-meta-time", title: "Total time spent on this project \u2014 sum across every task. Edit a task's time by double-clicking the time pill on its card.", children: [_jsx("span", { className: "meta-label", children: "Time spent:" }), _jsx("strong", { children: formatMinutes(tree.total_minutes_spent ?? 0) })] })] }))] }), _jsx("div", { className: "project-hero-actions", children: canManage && (_jsxs("div", { className: "project-hero-menu", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: "btn-icon", "aria-label": "Project actions", onClick: () => setMenuOpen((v) => !v), title: "More actions", children: "\u22EF" }), menuOpen && (_jsxs("div", { className: "project-hero-menu-dropdown", role: "menu", children: [tree.status === "completed" ? (_jsx("button", { type: "button", role: "menuitem", onClick: async () => {
                                                        setMenuOpen(false);
                                                        try {
                                                            await api.patch(`/projects/${tree.id}`, {
                                                                status: "active",
                                                            });
                                                            onChanged();
                                                        }
                                                        catch (e) {
                                                            notify.error(e instanceof Error
                                                                ? e.message
                                                                : "Reopen failed");
                                                        }
                                                    }, children: "\u21BB Reopen project" })) : (_jsx("button", { type: "button", role: "menuitem", onClick: async () => {
                                                        setMenuOpen(false);
                                                        // One-off tasks / one-time events are single-item
                                                        // containers — completing the project completes
                                                        // the one underlying task. Confirm + capture how
                                                        // long it took (logged on the task's extra
                                                        // minutes) rather than the multi-task
                                                        // "drops into wins" framing.
                                                        const isSingleItem = tree.type === "one_off_task" ||
                                                            tree.type === "one_time_event";
                                                        let timeSpentMinutes;
                                                        if (isSingleItem) {
                                                            if (!confirm(`Mark "${tree.name}" complete?`)) {
                                                                return;
                                                            }
                                                            const raw = prompt("How long did it take? Enter minutes (optional — leave blank to skip).", "");
                                                            if (raw !== null && raw.trim() !== "") {
                                                                const n = Math.round(Number(raw));
                                                                if (Number.isFinite(n) && n >= 0) {
                                                                    timeSpentMinutes = n;
                                                                }
                                                            }
                                                        }
                                                        else if (!confirm(`Mark "${tree.name}" complete? It'll drop out of the scheduler and appear in this month's wins on the Momentum page.`)) {
                                                            return;
                                                        }
                                                        try {
                                                            await api.patch(`/projects/${tree.id}`, {
                                                                status: "completed",
                                                                ...(timeSpentMinutes !== undefined
                                                                    ? { time_spent_minutes: timeSpentMinutes }
                                                                    : {}),
                                                            });
                                                            onChanged();
                                                        }
                                                        catch (e) {
                                                            // Backend returns a 409 with detail =
                                                            // { code, message, open_tasks } when work is
                                                            // still open. Surface the open titles so
                                                            // the user knows exactly what to close, not
                                                            // just "couldn't complete".
                                                            const err = e;
                                                            const detail = err.body?.detail;
                                                            if (detail && Array.isArray(detail.open_tasks)) {
                                                                const titles = detail.open_tasks
                                                                    .slice(0, 10)
                                                                    .map((t) => `  • ${t}`)
                                                                    .join("\n");
                                                                const more = detail.open_tasks.length > 10
                                                                    ? `\n  …and ${detail.open_tasks.length - 10} more`
                                                                    : "";
                                                                notify.error(`${detail.message ?? "Can't complete yet."}\n\nStill open:\n${titles}${more}`);
                                                            }
                                                            else {
                                                                notify.error(err.message ?? "Couldn't complete the project.");
                                                            }
                                                        }
                                                    }, children: "\u2713 Mark complete" })), _jsx("button", { type: "button", role: "menuitem", className: "btn-danger", onClick: () => {
                                                        setMenuOpen(false);
                                                        onDelete();
                                                    }, children: "Delete project" }), _jsx("div", { className: "muted small project-hero-menu-hint", children: "Removes the project, all its tasks, and any calendar blocks it generated. Cannot be undone." })] }))] })) })] }), _jsx(DescriptionEditor, { value: tree.description, canEdit: canManage, placeholder: "What this project is for, scope, expectations, links \u2014 anything users should know.", onSave: async (next) => {
                            await api.patch(`/projects/${tree.id}`, { description: next });
                            onChanged();
                        } }), isRecurring && (_jsx("p", { className: "muted small project-hero-hint", children: "Items in this project schedule as recurring events on team members' calendars instead of one-off blocks. Cadence lives on the meeting task below." })), !isOneTime && (_jsx(ProjectAssignees, { tree: tree, members: members, canManage: canManage, currentUserId: currentUserId, onChanged: onChanged }))] }), isOneTime && (_jsx(OneTimeMeetingPanel, { tree: tree, canEdit: canManage || currentUserId === tree.owner_id, onChanged: onChanged })), _jsx(ProjectResourcesPanel, { projectId: tree.id, canManage: canManage }), !isRecurring && !isOneOffTask && !isOneTime && (_jsx(RoadmapView, { projectId: tree.id, canManage: canManage, 
                // Re-fetch the roadmap whenever the project's task or
                // checklist-item set changes — onChanged reloads `tree`, this
                // signal changes, and RoadmapView's load() effect re-runs. No
                // manual page refresh needed after adding a task.
                refreshKey: `${tree.tasks.length}:${tree.tasks.reduce((s, t) => s + (t.checklist_items?.length ?? 0), 0)}` })), !isOneTime && !isOneOffTask && (_jsxs("section", { className: "project-tasks-section", children: [_jsxs("div", { className: "project-tasks-head", children: [_jsx("h3", { children: isOneOffTask ? "Task" : "Tasks" }), canManage && !isOneOffTask && (_jsx(ProjectAddTaskInline, { projectId: tree.id, members: members, existingTasks: tree.tasks, onChanged: onChanged }))] }), _jsxs("div", { className: "task-list", children: [(() => {
                                const topLevel = tree.tasks.filter((t) => !t.parent_task_id);
                                const rowProps = {
                                    allTasks: tree.tasks,
                                    members,
                                    isAdmin: canManage,
                                    currentUserId,
                                    projectType: tree.type,
                                    projectId: tree.id,
                                    projectOwnerId: tree.owner_id,
                                    depth: 0,
                                    onChanged,
                                };
                                // Ready/Waiting grouping + "N of total" numbering applies to
                                // standard projects (a real task flow). Meetings / one-off /
                                // one-time projects keep the plain backend order — their
                                // "tasks" aren't a dependency sequence.
                                if (tree.type !== "standard") {
                                    return topLevel.map((t) => (_jsx(TaskRow, { task: t, ...rowProps }, t.id)));
                                }
                                const { ordered, readyCount } = orderProjectTasks(topLevel);
                                const total = ordered.length;
                                const hasWaiting = readyCount < total;
                                return ordered.map((t, i) => (_jsxs(Fragment, { children: [hasWaiting && i === 0 && (_jsxs("div", { className: "task-group-label", children: ["\u25CF Ready \u00B7 ", readyCount] })), hasWaiting && i === readyCount && (_jsxs("div", { className: "task-group-label task-group-label-waiting", children: ["\u25F7 Waiting on dependencies \u00B7 ", total - readyCount] })), _jsx(TaskRow, { task: t, ...rowProps, flowPosition: i + 1, flowTotal: total, isWaiting: i >= readyCount, predecessorTitles: t.predecessor_titles ?? [] })] }, t.id)));
                            })(), tree.tasks.length === 0 && (_jsx("div", { className: "muted small project-tasks-empty", children: "No tasks yet \u2014 add one above to start the roadmap." }))] })] }))] }));
}
// "+ Add task" trigger + modal popup. The button lives in the project
// detail's Tasks header; clicking it opens a centered modal with Task
// Name, Task Notes, Assignees, and a Checklist builder. We use a modal
// rather than an inline expand so long forms don't push the task list
// off the page and so the input always lives in the same place visually.
function ProjectAddTaskInline({ projectId, members, existingTasks, onChanged, }) {
    const [open, setOpen] = useState(false);
    return (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "btn-primary", onClick: () => setOpen(true), children: "+ Add task" }), open && (_jsx(AddTaskModal, { projectId: projectId, members: members, existingTasks: existingTasks, onClose: () => setOpen(false), onSaved: () => {
                    setOpen(false);
                    onChanged();
                } }))] }));
}
function AddTaskModal({ projectId, members, existingTasks, onClose, onSaved, }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [assigneeIds, setAssigneeIds] = useState([]);
    // Multi-predecessor picker. Backend supports many-to-many task
    // dependencies (task_dependencies join table) — every chip toggled
    // here becomes one edge "this new task depends on that task". The
    // backend's POST /tasks accepts predecessor_task_ids; when present
    // it skips the default auto-chain (which would otherwise wire the
    // new task to the last existing task in the project).
    const [predIds, setPredIds] = useState([]);
    // Filter the picker by status — completed tasks rarely make sense
    // as a "wait for" gate. Hidden completed tasks can still be added
    // by flipping the "Include completed" toggle.
    const [includeCompletedDeps, setIncludeCompletedDeps] = useState(false);
    function togglePred(id) {
        setPredIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }
    // Local checklist drafts — title + per-item assignee.
    // Empty rows are skipped on submit so trailing blanks don't create
    // junk records. `done` previews "checking off" steps while building
    // the task, matching the post-save checklist UI.
    const [items, setItems] = useState([{ title: "", assignee_id: "", done: false }]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    async function submit() {
        if (!title.trim()) {
            setError("Task name is required.");
            return;
        }
        const realItems = items.filter((it) => it.title.trim());
        setBusy(true);
        setError(null);
        try {
            // 1 — create the parent task with title + notes + due_date +
            // predecessor edges. New tasks start with NO dependency — they land in
            // the "Ready" group until an editor explicitly wires one up (here via
            // the predecessor chips, or later on the roadmap). auto_chain:false
            // opts out of the backend's default linear auto-chain so a task isn't
            // silently wired onto the previously-added one.
            const t = await api.post("/tasks", {
                project_id: projectId,
                title: title.trim(),
                description: description.trim() || null,
                due_date: dueDate || null,
                auto_chain: false,
                ...(predIds.length > 0
                    ? { predecessor_task_ids: predIds }
                    : {}),
            });
            // 2 — assign each picked teammate (idempotent server-side).
            for (const uid of assigneeIds) {
                await api.post(`/tasks/${t.id}/assignees`, { user_id: uid });
            }
            // 3 — create each non-empty checklist item under the task. Carries
            // the local "done" preview state through so a user can pre-tick
            // items they've already completed before the task was tracked.
            for (const it of realItems) {
                const ci = await api.post("/tasks/checklist-items", {
                    task_id: t.id,
                    title: it.title.trim(),
                    assignee_id: it.assignee_id || null,
                });
                if (it.done) {
                    await api.patch(`/tasks/checklist-items/${ci.id}`, {
                        is_completed: true,
                    });
                }
            }
            onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't add the task");
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "New task" }), _jsxs("label", { children: ["Task name", _jsx("input", { autoFocus: true, value: title, onChange: (e) => setTitle(e.target.value), placeholder: "What's the next thing this project needs?" })] }), _jsxs("label", { children: ["Task notes ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("textarea", { rows: 3, value: description, onChange: (e) => setDescription(e.target.value), placeholder: "Context, links, success criteria \u2014 anything teammates should know." })] }), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Due date ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("input", { type: "date", value: dueDate, onChange: (e) => setDueDate(e.target.value) })] }), _jsxs("label", { children: ["Assign people ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx(MultiSelect, { options: members.map((m) => ({ id: m.id, label: m.name })), values: assigneeIds, onChange: setAssigneeIds, placeholder: "Pick teammates", renderTrigger: (sel) => (_jsx("span", { className: "pill assignees-pill", children: sel.length === 0
                                            ? "Unassigned"
                                            : `${sel.length} on it` })) })] })] }), _jsxs("div", { className: "add-task-deps", children: [_jsxs("div", { className: "add-task-deps-head", children: [_jsx("strong", { children: "Depends on" }), _jsx("span", { className: "muted small", children: "\u00B7 optional \u00B7 pick any number of tasks that must finish first" })] }), (() => {
                            const candidates = existingTasks
                                .filter((t) => !t.parent_task_id) // top-level only
                                .filter((t) => includeCompletedDeps || t.status !== "completed");
                            if (existingTasks.filter((t) => !t.parent_task_id).length === 0) {
                                return (_jsx("p", { className: "muted small add-task-deps-empty", children: "This is the first task \u2014 nothing to depend on yet." }));
                            }
                            return (_jsxs(_Fragment, { children: [_jsx("div", { className: "add-task-deps-chips", children: candidates.length === 0 ? (_jsx("span", { className: "muted small", children: "Every existing task is already completed." })) : (candidates.map((t) => {
                                            const isOn = predIds.includes(t.id);
                                            const isDone = t.status === "completed";
                                            return (_jsxs("button", { type: "button", className: `add-task-dep-chip ${isOn ? "active" : ""} ${isDone ? "done" : ""}`, onClick: () => togglePred(t.id), title: isOn
                                                    ? "Click to remove this prerequisite"
                                                    : "Click to mark this task as a prerequisite", children: [isOn ? "✓ " : "+ ", t.title, isDone && (_jsxs("span", { className: "muted small", children: [" ", "\u00B7 done"] }))] }, t.id));
                                        })) }), _jsxs("label", { className: "add-task-deps-toggle", children: [_jsx("input", { type: "checkbox", checked: includeCompletedDeps, onChange: (e) => setIncludeCompletedDeps(e.target.checked) }), _jsx("span", { className: "muted small", children: "Include already-completed tasks" })] }), predIds.length > 1 && (_jsxs("p", { className: "muted small add-task-deps-hint", children: ["All ", predIds.length, " prerequisites must close before this task becomes schedulable."] }))] }));
                        })()] }), _jsxs("div", { className: "add-task-checklist", children: [_jsxs("div", { className: "add-task-checklist-head", children: [_jsx("strong", { children: "Checklist" }), _jsx("span", { className: "muted small", children: "Sub-steps that can be checked off as the task progresses." })] }), items.map((it, idx) => (_jsxs("div", { className: "add-task-checklist-row", children: [_jsx("button", { type: "button", className: `add-task-checkbox ${it.done ? "done" : ""}`, "aria-checked": it.done, role: "checkbox", title: it.done
                                        ? "Mark this step incomplete"
                                        : "Mark this step complete", onClick: () => {
                                        const next = [...items];
                                        next[idx] = { ...next[idx], done: !next[idx].done };
                                        setItems(next);
                                    }, children: it.done ? "✓" : "" }), _jsx("input", { value: it.title, placeholder: `Step ${idx + 1}`, onChange: (e) => {
                                        const next = [...items];
                                        next[idx] = { ...next[idx], title: e.target.value };
                                        setItems(next);
                                    }, style: it.done
                                        ? { textDecoration: "line-through", opacity: 0.7 }
                                        : undefined }), _jsxs("select", { value: it.assignee_id, onChange: (e) => {
                                        const next = [...items];
                                        next[idx] = { ...next[idx], assignee_id: e.target.value };
                                        setItems(next);
                                    }, children: [_jsx("option", { value: "", children: "\u2014 assign \u2014" }), members.map((m) => (_jsx("option", { value: m.id, children: m.name }, m.id)))] }), items.length > 1 && (_jsx("button", { type: "button", className: "btn-link", onClick: () => setItems((cur) => cur.filter((_, i) => i !== idx)), title: "Remove this step", children: "\u2715" }))] }, idx))), _jsx("button", { type: "button", className: "btn-link", onClick: () => setItems((cur) => [
                                ...cur,
                                { title: "", assignee_id: "", done: false },
                            ]), children: "+ Add checklist item" })] }), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: onClose, disabled: busy, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", onClick: submit, disabled: busy || !title.trim(), children: busy ? "Adding…" : "Add task" })] })] }) }));
}
// Inline-editable BU + Activity chips for the project hero eyebrow.
// Admin/manager see two compact dropdowns that auto-save on change;
// workers see read-only labels. Either label is omitted when the field
// is null AND the user isn't allowed to edit (otherwise the picker
// always renders so admins can fill in a missing tag).
function ProjectBuActivityEditor({ tree, units, activities, canEdit, onChanged, unitName, activityName, }) {
    async function setUnit(next) {
        const v = next || null;
        if (v === (tree.business_unit_id ?? null))
            return;
        await api.patch(`/projects/${tree.id}`, { business_unit_id: v });
        onChanged();
    }
    async function setActivity(next) {
        const v = next || null;
        if (v === (tree.activity_id ?? null))
            return;
        await api.patch(`/projects/${tree.id}`, { activity_id: v });
        onChanged();
    }
    if (!canEdit) {
        // Worker view — fall back to the previous static label behavior. No
        // visible chip when both are null so the eyebrow doesn't gain
        // dangling separators.
        const parts = [];
        if (unitName)
            parts.push(unitName);
        if (activityName)
            parts.push(activityName);
        return _jsx("span", { children: parts.join(" · ") });
    }
    return (_jsxs("span", { className: "project-hero-bu-activity-edit", children: [_jsxs("select", { value: tree.business_unit_id ?? "", onChange: (e) => setUnit(e.target.value), title: "Change this project's business unit", children: [_jsx("option", { value: "", children: "\u2014 no BU \u2014" }), units.map((u) => (_jsx("option", { value: u.id, children: u.name }, u.id)))] }), _jsx("span", { children: " \u00B7 " }), _jsxs("select", { value: tree.activity_id ?? "", onChange: (e) => setActivity(e.target.value), title: "Change this project's activity tag", children: [_jsx("option", { value: "", children: "\u2014 no activity \u2014" }), activities.map((a) => (_jsx("option", { value: a.id, children: a.name }, a.id)))] })] }));
}
// Inline-editable project title. Admins/managers click the name (or the
// pencil) to turn the <h1> into a text input that saves on blur / Enter
// and reverts on Escape. Workers just see the static heading. Backend
// PATCH /projects/{id} already accepts `name` (admin/manager guarded), so
// this is a thin client wrapper over the same endpoint the due-date /
// priority editors use.
function ProjectNameEditor({ tree, canEdit, onChanged, }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(tree.name);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);
    // Re-sync the draft whenever the upstream name changes (switching
    // projects, or another admin renamed it) so we don't show a stale value.
    useEffect(() => {
        setValue(tree.name);
    }, [tree.name]);
    useEffect(() => {
        if (editing)
            inputRef.current?.focus();
    }, [editing]);
    async function save() {
        const next = value.trim();
        if (!next || next === tree.name) {
            setValue(tree.name);
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/projects/${tree.id}`, { name: next });
            setEditing(false);
            onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't rename the project.");
            setValue(tree.name);
        }
        finally {
            setSaving(false);
        }
    }
    if (!canEdit)
        return _jsx("h1", { children: tree.name });
    if (editing) {
        return (_jsx("input", { ref: inputRef, className: "project-hero-name-input", value: value, disabled: saving, onChange: (e) => setValue(e.target.value), onBlur: save, onKeyDown: (e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    void save();
                }
                else if (e.key === "Escape") {
                    e.preventDefault();
                    setValue(tree.name);
                    setEditing(false);
                }
            }, "aria-label": "Project name" }));
    }
    return (_jsxs("h1", { className: "project-hero-name-editable", onClick: () => setEditing(true), title: "Click to rename this project", children: [tree.name, _jsx("button", { type: "button", className: "project-hero-name-edit-btn", onClick: (e) => {
                    e.stopPropagation();
                    setEditing(true);
                }, "aria-label": "Rename project", children: "\u270E" })] }));
}
function ProjectDueDateRow({ tree, isAdmin, onChanged, }) {
    // Bare control — the surrounding card provides the "Due date" label so this
    // component just renders the input (admin) or read-only value (worker).
    const [value, setValue] = useState(tree.due_date ?? "");
    // Re-sync on project switch / upstream change — same stale-state bug the
    // priority row had: without this the field kept the previously-viewed
    // project's due date.
    useEffect(() => {
        setValue(tree.due_date ?? "");
    }, [tree.id, tree.due_date]);
    async function save() {
        if (value === (tree.due_date ?? ""))
            return;
        await api.patch(`/projects/${tree.id}`, { due_date: value || null });
        onChanged();
    }
    if (isAdmin) {
        return (_jsx("input", { type: "date", value: value, onChange: (e) => setValue(e.target.value), onBlur: save }));
    }
    return _jsx("span", { children: tree.due_date ?? "—" });
}
function ProjectPriorityRow({ tree, isAdmin, onChanged, }) {
    // Bare control — outer card supplies the "Priority" label and explanation.
    // Priority is now nullable (N/A = no rank). The dropdown renders "—" for
    // null; we keep local state as `number | null` to match.
    const [value, setValue] = useState(tree.priority);
    // Re-sync when the selected project changes. ProjectDetail isn't re-keyed
    // per project, so without this the dropdown kept showing the PREVIOUS
    // project's priority — a P2 project read as P5 (the default), so teammates
    // saw the wrong rank and didn't prioritize it. Mirror the upstream value
    // whenever it changes (project switch, or another admin's edit).
    useEffect(() => {
        setValue(tree.priority);
    }, [tree.id, tree.priority]);
    async function save(next) {
        if (next === tree.priority)
            return;
        setValue(next);
        await api.patch(`/projects/${tree.id}`, { priority: next });
        onChanged();
    }
    if (isAdmin) {
        // Empty string represents N/A (NULL on the server). Picking it
        // clears the priority — sorts the project last in priority ties.
        return (_jsxs("select", { value: value == null ? "" : String(value), onChange: (e) => save(e.target.value === "" ? null : Number(e.target.value)), children: [_jsx("option", { value: "", children: "\u2014 (N/A)" }), [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (_jsxs("option", { value: n, children: [n, n === 1 ? " (urgent)" : n === 10 ? " (least urgent)" : ""] }, n)))] }));
    }
    return (_jsx("span", { children: tree.priority == null ? "N/A" : `${tree.priority} / 10` }));
}
function ProjectAssignees({ tree, members, canManage, currentUserId, onChanged, }) {
    const assignees = members.filter((m) => tree.assignee_ids.includes(m.id));
    const isSelfAssigned = currentUserId != null && tree.assignee_ids.includes(currentUserId);
    async function applyAssigneeDiff(next) {
        // Compute add/remove diff vs. current set and fire per-id endpoints. Each
        // call is a single POST/DELETE so partial failures don't leave a
        // half-applied bulk update.
        const old = tree.assignee_ids;
        const added = next.filter((id) => !old.includes(id));
        const removed = old.filter((id) => !next.includes(id));
        for (const id of added) {
            await api.post(`/projects/${tree.id}/assignees`, { user_id: id });
        }
        for (const id of removed) {
            await api.del(`/projects/${tree.id}/assignees/${id}`);
        }
        // If this is a recurring-event project and there's at least one task with
        // cadence set, also auto-push the meeting onto the newly-added people's
        // calendars. The schedule-for-assignees endpoint falls back to project
        // assignees when the task itself has none, so this works whether the
        // admin manages assignment at task level or project level. Best-effort:
        // we still call onChanged() afterward so the UI refreshes even if a push
        // fails (admin can re-trigger from the meeting block).
        if (added.length > 0 && tree.type === "recurring_event") {
            const meetingTasks = (tree.tasks ?? []).filter((t) => !t.parent_task_id && t.recurrence_frequency);
            for (const t of meetingTasks) {
                try {
                    await api.post(`/tasks/${t.id}/schedule-for-assignees`);
                }
                catch {
                    /* best-effort */
                }
            }
        }
        onChanged();
    }
    async function selfClaim() {
        await api.post(`/projects/${tree.id}/claim`);
        onChanged();
    }
    async function selfUnclaim() {
        if (!currentUserId)
            return;
        await api.del(`/projects/${tree.id}/assignees/${currentUserId}`);
        onChanged();
    }
    return (_jsxs("div", { className: "due-date-row project-assignees-row", style: { flexWrap: "wrap" }, children: [_jsx("span", { className: "muted", children: "People on this project:" }), assignees.length === 0 ? (_jsx("span", { className: "muted", children: "no one yet" })) : (assignees.map((m) => _jsx("span", { className: "pill", children: m.name }, m.id))), canManage ? (_jsx("div", { style: { marginLeft: "auto" }, children: _jsx(MultiSelect, { options: members.map((m) => ({ id: m.id, label: m.name })), values: tree.assignee_ids, onChange: applyAssigneeDiff, placeholder: "+ Add / remove people", renderTrigger: (sel) => (_jsx("span", { children: sel.length === 0 ? "+ Add / remove" : `Edit (${sel.length})` })) }) })) : (currentUserId && (_jsx("span", { style: { marginLeft: "auto" }, children: isSelfAssigned ? (_jsx("button", { className: "btn-link", onClick: selfUnclaim, children: "Leave project" })) : (_jsx("button", { className: "btn-link", onClick: selfClaim, children: "Join project" })) })))] }));
}
